// src/MobileMenu.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FaBars, FaClock } from 'react-icons/fa6';
import { TbChartHistogram, TbMapPinExclamation, TbHomePlus, TbFilePencil, TbUserQuestion, TbUsers, TbTent, TbCheck, TbClock, TbBell, TbShield, TbShieldCheck, TbUser, TbSettings, TbLogout } from 'react-icons/tb';
import { LuMegaphone } from 'react-icons/lu';
import { FaRegIdCard } from 'react-icons/fa';
import { useAuth } from './AuthContext';

const mobileNavItems = [
  { label: 'Dashboard', icon: <TbChartHistogram />, path: '/dashboard' },
  { label: 'Incident Map', icon: <TbMapPinExclamation />, path: '/maps/incident' },
  { label: 'Evacuation Map', icon: <TbHomePlus />, path: '/maps/evacuation' },
  { label: 'Reports', icon: <TbFilePencil />, path: '/incident-reports' },
  { label: 'Announcements', icon: <LuMegaphone />, path: '/announcements' },
  { label: 'Missing Reports', icon: <TbUserQuestion />, path: '/missing-reports' },
  { label: 'Found Reports', icon: <TbUsers />, path: '/missing-report-interactions' },
  { label: 'Evacuation Shelters', icon: <TbHomePlus />, path: '/evacuation-shelters' },
  { label: 'Approved Shelters', icon: <TbCheck />, path: '/temporary-shelters/approved' },
  { label: 'Pending Shelters', icon: <TbClock />, path: '/temporary-shelters/pending' },
  { label: 'SOS Beacons', icon: <TbBell />, path: '/sos-beacons' },
  { label: 'Applications', icon: <TbClock />, path: '/responder-applications' },
  { label: 'Approved Responders', icon: <TbShieldCheck />, path: '/approved-responders' },
  { label: 'Incident Tracking', icon: <TbChartHistogram />, path: '/responder-incident-tracking' },
  { label: 'Responder Map', icon: <TbMapPinExclamation />, path: '/responder-map' },
  { label: 'Manage Users', icon: <TbUsers />, path: '/manage-users' },
  { label: 'ID Verification', icon: <FaRegIdCard />, path: '/id-verification' },
  { label: 'Profile Settings', icon: <TbUser />, path: '/profile' },
  { label: 'Settings', icon: <TbSettings />, path: '/settings' },
  { label: 'Logout', icon: <TbLogout />, path: '/logout', isLogout: true }
];

export default function MobileMenu({ isOpen, onClose }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { signOut } = useAuth();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Check screen size and close menu if not mobile
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      
      // Close menu if screen becomes larger than mobile
      if (!mobile && isOpen) {
        onClose();
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isOpen, onClose]);

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isOpen && !event.target.closest('.mobile-menu-overlay')) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      // Don't manipulate body scroll to avoid zoom issues
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  // Close menu on escape key
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  const handleNavClick = (item) => {
    if (item.isLogout) {
      // Handle logout
      signOut();
      navigate('/login');
    } else {
      navigate(item.path);
      onClose(); // Close menu after navigation
    }
  };

  if (!isOpen || !isMobile) return null;

  return (
    <div className="mobile-menu-overlay">
      <div className="mobile-menu-content">
        {/* Mobile Navigation Items */}
        <div className="mobile-nav-items">
          {mobileNavItems.map((item, index) => {
            const isActive = location.pathname === item.path;
            return (
              <a
                key={index}
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  handleNavClick(item);
                }}
                className={`mobile-nav-item ${isActive ? 'active' : ''} ${item.isLogout ? 'logout-item' : ''}`}
                title={item.label}
              >
                <span className="mobile-nav-icon">
                  {item.icon}
                </span>
                <span className="mobile-nav-label">
                  {item.label}
                </span>
              </a>
            );
          })}
        </div>
      </div>
    </div>
  );
} 